import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { fetchPrivacyPolicy } from "../../store/slices/legalSlice";
import { fetchSEOPages } from "../../store/slices/seoSlice";

import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import LoaderWithContainer from "../../components/base-components/LoaderWithContainer";
import ErrorMessageWithContainer from "../../components/base-components/ErrorWithConatiner";
import NoDataWithContainer from "../../components/base-components/NoDataWithContainer";

const PrivacyPolicy: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { privacyPolicy, status, error } = useSelector(
    (state: RootState) => state.legal
  );

  const {
    seoPages,
    status: seoStatus,
    error: seoError,
  } = useSelector((state: RootState) => state.seoPages);

  useEffect(() => {
    dispatch(fetchPrivacyPolicy());

    if (seoStatus === "idle") {
      dispatch(fetchSEOPages());
    }
  }, [dispatch, seoStatus]);

  if (status === "loading") return <LoaderWithContainer />;
  if (status === "failed") return <ErrorMessageWithContainer message={error} />;
  if (seoStatus === "failed") {
    return <ErrorMessageWithContainer message={seoError} />;
  }

  if (!privacyPolicy) {
    return <NoDataWithContainer message="Privacy Policy not found" />;
  }

  const getSEOData = () => {
    return seoPages.find(
      (page) => `/${page.attributes.url}` === location.pathname
    )?.attributes;
  };

  const seo = getSEOData();

  return (
    <>
      <Helmet>
        <title>{seo?.seo_title}</title>
        {seo?.seo_description && (
          <meta name="description" content={seo.seo_description} />
        )}

        <link rel="canonical" href={window.location.href} />
        <meta
          property="og:image"
          content="https://www.dancap-group.co.uk/images/dancap-logo-dark.jpg"
        />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta property="og:title" content={seo?.seo_title} />
        <meta property="og:description" content={seo?.seo_description} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:image"
          content="https://www.dancap-group.co.uk/images/dancap-logo-dark.jpg"
        />
      </Helmet>

      <div className="global-container">
        <h1 className="mb-[4rem] h3 text-white text-center">Privacy Policy</h1>
        {privacyPolicy ? (
          <div className="bg-white rounded-[0.5rem] p-[2rem] mb-[5rem]">
            <BlocksRenderer content={privacyPolicy} />
          </div>
        ) : (
          <p>No Data</p>
        )}
      </div>
    </>
  );
};

export default PrivacyPolicy;
